import React from 'react';

import { Dialog as DialogUI, DialogContent, DialogDescription, DialogTitle } from '../../ui/dialog';
import { Button } from '../Button/Button';

export interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  description?: string;
  /** Defaults to "Confirm" */
  confirmText?: string;
  /** Defaults to "Cancel" */
  cancelText?: string;
  /** `danger` styles the confirm button red; keep it for destructive actions */
  variant?: 'default' | 'danger';
  /** Material Symbols icon name; defaults to a warning icon */
  icon?: string;
  /** Extra detail between the description and the buttons, e.g. a list of what's affected */
  children?: React.ReactNode;
  onConfirm: () => void;
  /** Also called when the dialog is dismissed (Escape, overlay click, close button) */
  onCancel: () => void;
}

/**
 * Asks the user to confirm an action. `<Form>` renders it for forms that pass
 * `confirm` to `useZodForm`; it can also be used on its own for button actions.
 * Built on the dialog primitives (not `Dialog`) to avoid a Form import cycle.
 */
export const ConfirmDialog = ({
  isOpen,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  icon = 'warning',
  children,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) => (
  <DialogUI open={isOpen} onOpenChange={(open) => !open && onCancel()}>
    <DialogContent>
      <div className="flex flex-col items-center mx-auto w-full text-center">
        <div
          className={`grid px-5 py-4 mb-2.5 rounded-full place-items-center ${
            variant === 'danger' ? 'bg-red-100' : 'bg-blue-100 dark:bg-blue-900'
          }`}
        >
          <span
            aria-hidden
            className={`block text-3xl material-symbols-outlined ${
              variant === 'danger' ? 'text-red-700' : 'text-blue-700 dark:text-blue-200'
            }`}
          >
            {icon}
          </span>
        </div>
        <DialogTitle className="mb-2 text-xl font-bold sm:text-2xl">{title}</DialogTitle>
        {description && <DialogDescription className="text-base">{description}</DialogDescription>}
      </div>

      {children}

      <div className="flex flex-col-reverse gap-5 justify-center items-center mt-4 w-full sm:flex-row">
        {/* Explicit loading/disabled: inside a submitting form the Button would otherwise show as busy */}
        <Button
          type="button"
          variant="secondary"
          loading={false}
          disabled={false}
          onClick={onCancel}
        >
          {cancelText}
        </Button>
        <Button
          type="button"
          variant={variant === 'danger' ? 'danger' : 'primary'}
          loading={false}
          disabled={false}
          onClick={onConfirm}
        >
          {confirmText}
        </Button>
      </div>
    </DialogContent>
  </DialogUI>
);
